import React, { useContext, useMemo, useState } from 'react';
import { GDAIChatSource, NovaChatMessage } from '@/types';
import {
  novaThemes,
  novaDisclaimer,
  novaRichTextTableClassName,
  novaRichTextHeadingSpacingClassName,
  novaSectionSubheadingClassName,
  novaTableVars,
  NovaThemeName,
} from './novaTheme';
import {
  parseNovaAnswer,
  extractInlineSources,
  mergeSources,
  mergeSuggestedQuestions,
  dedupeSourcesByLink,
  styleStreamingHeadings,
  buildNovaExportHtml,
  linkCitationMarkers,
  findImageSource,
  imageCaption,
} from './novaAnswerParser';
import { useNovaResponseActions } from '@/hooks/useNovaResponseActions';
import Tooltip from '../general/Tooltip';
import NovaThinkingIndicator from './NovaThinkingIndicator';
import NovaMark, { answerMarkState } from './NovaMark';
import NovaToast from './NovaToast';
import NovaInfoDialog from './NovaInfoDialog';
import NovaDownloadDialog from './NovaDownloadDialog';
import { NovaCopyIcon, NovaDownloadIcon, NovaGridIcon, NovaInfoIcon } from './novaIcons';
import { AppInsightsContext, TrackEventKey, TrackEventName, TrackEventType, UserContext } from '@/components';

// The gallery preview shows at most this many images inline, with the rest reachable
// via "View all images". Kept to 2 (rather than the buildout spec's original 4)
// so each preview image gets a wide-enough cell to render in full — see the
// object-contain/object-cover note below.
const GALLERY_PREVIEW_COUNT = 2;

const SECTION_HEADING_CLASS =
  'text-[24px] leading-[30px] font-semibold tracking-[-0.4px] md:text-[30px] md:leading-[36px] md:tracking-[-0.7px]';
const SUBSECTION_HEADING_CLASS =
  'text-[19px] leading-[25px] font-semibold tracking-[-0.2px] md:text-[21px] md:leading-[28px] md:tracking-[-0.3px]';

interface NovaAnswerCardProps {
  question?: string;
  message?: NovaChatMessage;
  waiting: boolean;
  /** Still receiving tokens for this answer. */
  streaming?: boolean;
  leanIn?: boolean;
  theme: NovaThemeName;
  followUpQuestions?: string[];
  onFollowUpClick?: (question: string) => void;
  onSave?: () => void;
  saveLabel?: string;
  published12mFilter?: boolean;
}

// The Nova.ai "report card" answer layout: headline, subtitle, optional image
// gallery, numbered sections — falling back to a plain block when the response
// doesn't have heading structure. Only used on the full-page NovaLanding; the
// floating NovaPanel keeps its compact chat-bubble style (NovaMessage).
const NovaAnswerCard: React.FC<NovaAnswerCardProps> = ({
  question,
  message,
  waiting,
  streaming,
  leanIn,
  theme,
  followUpQuestions,
  onFollowUpClick,
  onSave,
  saveLabel,
  published12mFilter,
}) => {
  const surfaces = novaThemes[theme];
  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);

  const content = message?.content ?? '';
  // An answer can be images/sources-only (no text tokens at all) — still real
  // content, not the "couldn't answer" empty state below.
  const hasContent =
    content.trim().length > 0 || (message?.images?.length ?? 0) > 0 || (message?.sources?.length ?? 0) > 0;
  // While tokens are still arriving, the answer renders as one plain flowing block
  // instead of the headline/sections report card — re-deriving that structure on
  // every update makes the text jump around (a line flips between body, 40px
  // headline and numbered section as its markdown resolves, the number column pops
  // in once a second section appears). The structured layout is built once, at the end.
  const isStreamingAnswer = !!streaming && hasContent;
  const answer = useMemo(() => parseNovaAnswer(isStreamingAnswer ? '' : content), [content, isStreamingAnswer]);
  // Same citation-badge pass as the final layout below — otherwise, while streaming,
  // [n] markers (and any link marked resolved them into) show as plain blue link text.
  const streamingHtml = useMemo(() => {
    if (!isStreamingAnswer) return '';
    const inline = extractInlineSources(content);
    return styleStreamingHeadings(
      linkCitationMarkers(inline.html, mergeSources(message?.sources, inline.parsedSources), message?.images ?? []),
    );
  }, [content, isStreamingAnswer, message?.sources, message?.images]);
  const sources: GDAIChatSource[] = useMemo(
    () => mergeSources(message?.sources, answer.parsedSources),
    [message?.sources, answer.parsedSources],
  );
  // One chip per article — the citations event can list one article several times
  // (text plus its figures); `sources` keeps every number so inline badges still resolve.
  const referenceSources = useMemo(() => dedupeSourcesByLink(sources), [sources]);
  // Copy/export HTML: no sources or "Related Questions" blocks of the model's own (those
  // are chips in the UI), and a References list of real links in their place.
  const images = useMemo(() => message?.images ?? [], [message?.images]);
  const htmlForCopy = useMemo(() => buildNovaExportHtml(content, sources, images), [content, sources, images]);
  const [galleryExpanded, setGalleryExpanded] = useState(false);
  const visibleImages = useMemo(
    () => (galleryExpanded ? images : images.slice(0, GALLERY_PREVIEW_COUNT)),
    [images, galleryExpanded],
  );

  // Each [n] marker only becomes a link once we know which actual source (or image
  // — see linkCitationMarkers) it names — that requires `sources`/`images`, which
  // aren't resolved until after parseNovaAnswer runs, so these fields are
  // re-processed here rather than inside the parser itself.
  const { preambleHtml, headlineHtml, subtitleHtml, bodyHtml } = useMemo(() => {
    const link = (text?: string) => (text ? linkCitationMarkers(text, sources, images) : text);
    return {
      preambleHtml: link(answer.preamble),
      headlineHtml: link(answer.headline),
      subtitleHtml: link(answer.subtitle),
      bodyHtml: link(answer.bodyHtml),
    };
  }, [answer.preamble, answer.headline, answer.subtitle, answer.bodyHtml, sources, images]);
  const sections = useMemo(
    () =>
      answer.sections.map((section) => ({
        ...section,
        heading: linkCitationMarkers(section.heading, sources, images),
        html: linkCitationMarkers(section.html, sources, images),
        groupLabel: section.groupLabel ? linkCitationMarkers(section.groupLabel, sources, images) : section.groupLabel,
      })),
    [answer.sections, sources, images],
  );
  // AVA doesn't actually send a structured `suggestedQuestions` field today (see
  // novaAnswerParser) — `followUpQuestions` (from the stream, always empty in
  // practice) wins if that ever changes, otherwise fall back to the model's own
  // "Related Questions" list parsed out of this answer's text.
  const effectiveFollowUpQuestions = useMemo(
    () => mergeSuggestedQuestions(followUpQuestions, answer.relatedQuestions),
    [followUpQuestions, answer.relatedQuestions],
  );

  const {
    copied,
    infoOpen,
    setInfoOpen,
    downloadOpen,
    setDownloadOpen,
    exportToast,
    exportError,
    exporting,
    copyToClipboard,
    handleExport,
  } = useNovaResponseActions(htmlForCopy);

  const trackReferenceClick = (source: GDAIChatSource) => {
    appInsights.trackCustomEvent(TrackEventName.NOVA, TrackEventKey.REFERENCE, TrackEventType.CLICK, user, {
      ref_link: source.meta_Link,
      ref_title: source.title,
    });
  };

  return (
    <div className="relative w-full max-w-[880px] px-1 pt-2">
      {question && (
        <div className="mb-6 flex justify-end md:mb-8">
          <div
            className="max-w-[85%] rounded-2xl rounded-br-[4px] px-4 py-3 text-[15px] leading-[22px] font-medium md:max-w-[70%] md:px-5 md:py-3.5 md:text-[17px] md:leading-[25px]"
            style={{ background: surfaces.ink.heading, color: surfaces.surfaces.card }}
          >
            {question}
          </div>
        </div>
      )}

      <div>
        {waiting && !hasContent ? (
          <NovaThinkingIndicator theme={theme} published12mFilter={published12mFilter} />
        ) : !waiting && !streaming && !hasContent ? (
          // The request for this question failed before any token arrived — show this
          // instead of silently rendering nothing (a toast already reported the error).
          <div className="text-sm" style={{ color: surfaces.ink.placeholder }}>
            Nova couldn&apos;t answer that. Please try again.
          </div>
        ) : isStreamingAnswer ? (
          <>
            {streamingHtml && (
              <div
                className={`richText text-[15px] [&_a]:no-underline [&_p]:!my-2 ${novaRichTextHeadingSpacingClassName} ${novaRichTextTableClassName}`}
                style={{ color: surfaces.ink.body, ...novaTableVars(surfaces) } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: streamingHtml }}
              />
            )}
            <NovaMark state="spin" theme={theme} size={100} className="mt-6" />
          </>
        ) : (
          // Fades in once, when the streamed plain block is swapped for the final
          // structured layout, so that single switch reads as a settle, not a jump.
          <div className="animate-nova-settle">
            {preambleHtml && (
              <div
                className={`richText mb-4 text-[15px] [&_a]:no-underline [&_a:hover]:text-[color:var(--nova-link-hover)] [&_p]:!my-2 ${novaRichTextTableClassName}`}
                style={{ color: surfaces.ink.body, ...novaTableVars(surfaces) } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: preambleHtml }}
              />
            )}

            {headlineHtml && (
              <h1
                className="mb-4 text-[28px] leading-[32px] font-semibold tracking-[-0.6px] text-pretty md:mb-6 md:text-[40px] md:leading-[44px] md:tracking-[-1.1px]"
                style={{ color: surfaces.ink.heading, ...novaTableVars(surfaces) } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: headlineHtml }}
              />
            )}

            {subtitleHtml && (
              // Deliberately fills the full 880px card width rather than the narrower
              // standfirst measure used elsewhere (e.g. the landing page's own
              // standfirst) — requested explicitly for this card.
              <div
                className="mb-5 text-base leading-[24px] text-pretty md:mb-[26px] md:text-lg md:leading-[27px]"
                style={{ color: surfaces.ink.standfirst, ...novaTableVars(surfaces) } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: subtitleHtml }}
              />
            )}

            {visibleImages.length > 0 && (
              <>
                <div className="mb-2 grid grid-cols-2 gap-2 md:gap-3">
                  {visibleImages.map((image) => {
                    const imageSource = findImageSource(image, sources);
                    const caption = imageCaption(image);
                    // The whole image shows letterboxed on its bubble background instead
                    // of having its edges cropped off to fill a fixed-height cell; the
                    // caption is pinned to the image's own bottom-left corner.
                    const thumbnail = (
                      <div className="flex h-[160px] items-center justify-center sm:h-[240px] md:h-[300px]">
                        <div className="relative max-h-full max-w-full">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={image.url} alt={caption} className="block max-h-[160px] max-w-full sm:max-h-[240px] md:max-h-[300px]" />
                          {caption && (
                            <div
                              className="absolute right-2 bottom-1.5 left-2 truncate text-xs font-semibold text-white md:right-3 md:bottom-2 md:left-3 md:text-sm"
                              style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}
                              title={caption}
                            >
                              {caption}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                    return (
                      <div
                        key={image.chunk_id}
                        className="overflow-hidden rounded-[14px]"
                        style={{ background: surfaces.surfaces.novaBubble }}
                      >
                        {imageSource ? (
                          <a
                            href={imageSource.meta_Link}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={imageSource.title ? `Open report: ${imageSource.title}` : 'Open source report'}
                            title={imageSource.title || undefined}
                            className="block cursor-pointer"
                            onClick={() => trackReferenceClick(imageSource)}
                          >
                            {thumbnail}
                          </a>
                        ) : (
                          thumbnail
                        )}
                      </div>
                    );
                  })}
                </div>

                {images.length > GALLERY_PREVIEW_COUNT && (
                  <div className="mb-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setGalleryExpanded((v) => !v)}
                      className="flex cursor-pointer items-center gap-1.5 text-sm font-medium"
                      style={{ color: surfaces.ink.heading }}
                    >
                      {galleryExpanded ? 'Show fewer images' : 'View all images'}
                      <NovaGridIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </>
            )}

            {bodyHtml && (
              <div
                className={`richText mb-6 text-[15px] [&_a]:no-underline [&_a:hover]:text-[color:var(--nova-link-hover)] [&_p]:!my-2 ${novaRichTextHeadingSpacingClassName} ${novaRichTextTableClassName}`}
                style={{ color: surfaces.ink.body, ...novaTableVars(surfaces) } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            )}

            {sections.length > 0 && (
              <div className="flex flex-col gap-5">
                {sections.map((section, index) => (
                  <div key={index} className="border-t pt-5" style={{ borderColor: surfaces.surfaces.hairline }}>
                    <div className="min-w-0">
                      {section.groupLabel && (
                        <div
                          className={`mb-4 ${SECTION_HEADING_CLASS}`}
                          style={{ color: surfaces.ink.heading }}
                          dangerouslySetInnerHTML={{ __html: section.groupLabel }}
                        />
                      )}
                      <div
                        className={`mb-2 ${section.subsection ? SUBSECTION_HEADING_CLASS : SECTION_HEADING_CLASS}`}
                        style={{ color: surfaces.ink.heading }}
                        dangerouslySetInnerHTML={{ __html: section.heading }}
                      />
                      <div
                        className={`richText text-[15px] [&_a]:no-underline [&_a:hover]:text-[color:var(--nova-link-hover)] [&_p]:!my-1.5 ${novaRichTextHeadingSpacingClassName} ${novaSectionSubheadingClassName} ${novaRichTextTableClassName}`}
                        style={{ color: surfaces.ink.body, ...novaTableVars(surfaces) } as React.CSSProperties}
                        dangerouslySetInnerHTML={{ __html: section.html }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {referenceSources.length > 0 && (
              <div className="mt-8 border-t pt-[18px]" style={{ borderColor: surfaces.surfaces.hairline }}>
                <div
                  className="mb-2.5 text-[11px] tracking-[0.14em] uppercase"
                  style={{ color: surfaces.ink.secondary }}
                >
                  Reference content
                </div>
                <div className="flex flex-wrap gap-2">
                  {referenceSources.map((source, index) => (
                    <a
                      key={index}
                      href={source.meta_Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-[9px] rounded-full border py-[7px] pr-3.5 pl-2 text-[13.5px] no-underline hover:text-[color:var(--nova-link-hover)]"
                      style={
                        {
                          background: surfaces.surfaces.field,
                          borderColor: surfaces.surfaces.panelBorder,
                          color: surfaces.ink.body,
                          ...novaTableVars(surfaces),
                        } as React.CSSProperties
                      }
                      onClick={() => trackReferenceClick(source)}
                    >
                      <span
                        className="flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-semibold"
                        style={{ background: surfaces.surfaces.userBubble, color: surfaces.ink.userBubbleText }}
                      >
                        {source.id}
                      </span>
                      {source.title}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {effectiveFollowUpQuestions.length > 0 && (
              <div className="mt-6">
                <div className="mb-2.5 text-[11px] tracking-[0.14em] uppercase" style={{ color: surfaces.ink.secondary }}>
                  Related Questions
                </div>
                <div className="flex flex-wrap gap-2">
                  {effectiveFollowUpQuestions.map((followUp, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => onFollowUpClick?.(followUp)}
                      className="cursor-pointer rounded-full border px-3 py-1 text-sm"
                      style={{ borderColor: surfaces.surfaces.panelBorder, color: surfaces.ink.body }}
                    >
                      {followUp}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {hasContent && !waiting && !streaming && (
              <div className="mt-6 flex items-center justify-between gap-3 pl-4 ">
                <div className="flex items-center gap-3" style={{ color: surfaces.ink.chromeIcon }}>
                  <Tooltip placement="top" content="Copy">
                    <button type="button" aria-label="Copy" onClick={copyToClipboard}>
                      <NovaCopyIcon className="h-4 w-4 cursor-pointer" />
                    </button>
                  </Tooltip>
                  <button type="button" aria-label="More info" onClick={() => setInfoOpen(true)}>
                    <NovaInfoIcon className="h-4 w-4 cursor-pointer" />
                  </button>
                  <Tooltip placement="top" content="Download">
                    <button type="button" aria-label="Download" onClick={() => setDownloadOpen(true)}>
                      <NovaDownloadIcon className="h-4 w-4 cursor-pointer" />
                    </button>
                  </Tooltip>
                </div>
                {onSave && (
                  <Tooltip placement="bottom" content={saveLabel || 'Save to scrapbook'}>
                    <button
                      type="button"
                      onClick={onSave}
                      className="cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium"
                      style={{ background: surfaces.actionAccent, color: surfaces.ink.onGold }}
                    >
                      {saveLabel || 'Save to scrapbook'}
                    </button>
                  </Tooltip>
                )}
              </div>
            )}

            {/* Mirrors Claude's own answer footer: the mark stays under the text the whole
                time, spinning while tokens are still arriving and settling into its resting
                breathing pulse once the answer is complete — never just popping in at the end. */}
            {hasContent && !waiting && (
              <NovaMark state={answerMarkState({ streaming, leanIn })} theme={theme} size={100} className="mt-6" />
            )}

            {hasContent && (
              <div className="mt-5 text-xs" style={{ color: surfaces.ink.placeholder }}>
                {novaDisclaimer.long}
              </div>
            )}
          </div>
        )}
      </div>

      {(copied || exportToast || exportError) && (
        <div className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2">
          <NovaToast
            theme={theme}
            variant={exportError ? 'error' : 'success'}
            message={exportError ? 'Export failed — please try again' : copied ? 'Copied with formatting' : 'Export complete'}
          />
        </div>
      )}

      <NovaInfoDialog
        open={infoOpen}
        theme={theme}
        searchTerm={question}
        createdAt={message?.timestamp}
        sourceCount={referenceSources.length}
        published12mFilter={published12mFilter}
        onClose={() => setInfoOpen(false)}
      />
      <NovaDownloadDialog
        open={downloadOpen}
        theme={theme}
        defaultName={question || 'Nova report'}
        onClose={() => setDownloadOpen(false)}
        exporting={exporting}
        onExport={handleExport}
      />
    </div>
  );
};

export default NovaAnswerCard;
